import type { TargetStatus } from "./campaigns.ts";

/**
 * Whether the Campaigns screen's lower half is the New campaign form or the card and
 * Line items panels (the one-screen layout, PR 1 of audits/2026-10-29/gui/plan.md).
 *
 * 🔴 The form and the panels share one half, so anything that opens a panel must give
 * the half back: with the form standing there, the row's Card, Edit and Line items — and
 * the card's own Line items button — changed state that nothing showed. The ordinary
 * path ran into it: create a campaign, the form stays open, press Line items on its row
 * as `LINE_ITEMS_HERE` says, and nothing happens (-da on twads#234). So an opening
 * closes the form, and so does a create X answered `created`: the new row is then in
 * the list, beside the panels it opens. A create that may exist (`exists`, `unknown`)
 * keeps the form, because its lock and its warning stand in it.
 */
export type LowerHalfEvent = "toggle" | "opened" | "created";

/** The form's next state, from its state now and what happened. */
export function formShownAfter(event: LowerHalfEvent, shown: boolean): boolean {
	switch (event) {
		case "toggle":
			return !shown;
		case "opened":
		case "created":
			return false;
	}
}

/**
 * Whether the card's panel stands beside the Line items panel, or the Line items panel
 * takes the whole lower half (the GUI integration round of audits/2026-10-29/gui/plan.md).
 *
 * 🔴 A line item's own screen — four panels — gets the lower half's full width while it is
 * open; the card is put aside, not unmounted, so an edit or a confirmation in it is still
 * there when the screen closes. And the rule above holds here too: an opening of the card
 * — the row's Card or Edit — closes the line item's screen, or the press would open a card
 * nothing shows.
 */
export function cardBeside(lineItemScreenOpen: boolean): boolean {
	return !lineItemScreenOpen;
}

/**
 * Whether the row's switch asks before it sends (PR 2 of audits/2026-10-29/gui/plan.md).
 *
 * 🔴 Start asks: the switch to ACTIVE is sent only from the Start of the confirmation,
 * which shows `enablePrompt` as it stands — the campaign, the account, the budgets, the
 * host, and that the line items stay as X holds them. Pause asks nothing, as it did
 * under `window.confirm`.
 */
export function asksFirst(target: TargetStatus): boolean {
	return target === "ACTIVE";
}
